import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { AddPhotoAlternate, Campaign, Close, Delete, Refresh, Schedule, Send } from '@mui/icons-material';

const AUDIENCES = [
  { value: 'customers', label: 'All customers' },
  { value: 'new_customers', label: 'New customers (last 30 days)' },
  { value: 'drivers', label: 'All drivers' },
  { value: 'online_drivers', label: 'Online approved drivers' },
  { value: 'offline_drivers', label: 'Offline approved drivers' },
  { value: 'all', label: 'Customers and drivers' }
];
const AUDIENCE_LABEL = Object.fromEntries(AUDIENCES.map(({ value, label }) => [value, label]));
const CATEGORIES = [
  { value: 'promotion', label: 'Promotional offer' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'service_update', label: 'Service update' }
];

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

const statusColor = (status) => ({
  scheduled: 'info', sending: 'warning', sent: 'success', partial: 'warning',
  failed: 'error', cancelled: 'default'
}[status] || 'default');

const formatDate = (value) => value ? new Date(value).toLocaleString() : '—';
const getRequestError = (error, fallback) => {
  if (error.response?.status === 404) {
    return 'The campaign API is missing from the backend currently running on port 5000. Restart the DrainGo backend from the repository root with `npm run dev`, then refresh this page.';
  }
  return error.response?.data?.message || fallback;
};

export default function PushCampaigns() {
  const [form, setForm] = useState({ title: '', body: '', category: 'announcement', audience: 'customers', zoneId: '', scheduledAt: '' });
  const [zones, setZones] = useState([]);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  // Image attachment state
  const [imagePreview, setImagePreview] = useState(null); // local data-URL for preview
  const [imageUploadUrl, setImageUploadUrl] = useState(null); // uploaded CDN URL
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef(null);

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await axios.get('/admin/push-campaigns');
      setCampaigns(data.campaigns || []);
      setError('');
    } catch (err) {
      setError(getRequestError(err, 'Could not load push campaign history.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  useEffect(() => {
    axios.get('/admin/zones')
      .then(({ data }) => setZones((Array.isArray(data) ? data : []).filter((zone) => zone.isActive)))
      .catch(() => setZones([]));
  }, []);

  useEffect(() => {
    let mounted = true;
    axios.get('/admin/push-campaigns/audience-counts', {
      params: { audience: form.audience, ...(form.zoneId ? { zoneId: form.zoneId } : {}) }
    })
      .then(({ data }) => { if (mounted) setPreview(data); })
      .catch(() => { if (mounted) setPreview(null); });
    return () => { mounted = false; };
  }, [form.audience, form.zoneId]);

  const hasActiveCampaign = useMemo(() => campaigns.some((item) => item.status === 'sending'), [campaigns]);
  useEffect(() => {
    if (!hasActiveCampaign) return undefined;
    const interval = setInterval(loadCampaigns, 5000);
    return () => clearInterval(interval);
  }, [hasActiveCampaign, loadCampaigns]);

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  // ─── Image pick & upload ─────────────────────────────────────────────────
  const handleImagePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageError('');

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setImageError('Please select a JPG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError('Image must be 8 MB or smaller.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      setImagePreview(dataUrl);
      setImageUploadUrl(null);

      // Upload immediately so we have the URL ready
      setImageUploading(true);
      try {
        const { data } = await axios.post('/admin/push-campaigns/upload-image', { image: dataUrl });
        setImageUploadUrl(data.url);
        setImageError('');
      } catch (err) {
        setImageError(getRequestError(err, 'Image upload failed. Please try again.'));
        setImagePreview(null);
      } finally {
        setImageUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageUploadUrl(null);
    setImageError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Campaign submit ──────────────────────────────────────────────────────
  const createCampaign = async (event) => {
    event.preventDefault();
    if (imageUploading) { setError('Please wait for the image to finish uploading.'); return; }
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category,
        audience: form.audience,
        ...(imageUploadUrl ? { imageUrl: imageUploadUrl } : {})
      };
      if (form.zoneId) payload.zoneId = form.zoneId;
      if (form.scheduledAt) payload.scheduledAt = new Date(form.scheduledAt).toISOString();
      const { data } = await axios.post('/admin/push-campaigns', payload);
      setNotice(data.message || 'Campaign created.');
      setForm((current) => ({ ...current, title: '', body: '', scheduledAt: '' }));
      clearImage();
      await loadCampaigns();
    } catch (err) {
      setError(getRequestError(err, 'Campaign could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  const openDetails = async (campaign) => {
    setSelected(campaign);
    setDeliveries([]);
    try {
      const { data } = await axios.get(`/admin/push-campaigns/${campaign._id}`);
      setSelected(data.campaign);
      setDeliveries(data.deliveries || []);
    } catch (err) {
      setError(getRequestError(err, 'Could not load delivery status.'));
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      const { data } = await axios.post(`/admin/push-campaigns/${campaign._id}/cancel`);
      setNotice(data.message || 'Campaign cancelled.');
      await loadCampaigns();
    } catch (err) {
      setError(getRequestError(err, 'Campaign could not be cancelled.'));
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!campaign) return;
    const confirmed = window.confirm(`Are you sure you want to delete campaign "${campaign.title}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const { data } = await axios.delete(`/admin/push-campaigns/${campaign._id}`);
      setNotice(data.message || 'Campaign deleted.');
      if (selected && selected._id === campaign._id) {
        setSelected(null);
      }
      await loadCampaigns();
    } catch (err) {
      setError(getRequestError(err, 'Campaign could not be deleted.'));
    }
  };

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0d2b0d' }}>Push campaigns</Typography>
          <Typography color="text.secondary">Send announcements and offers to selected customer or driver groups.</Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={loadCampaigns} variant="outlined">Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent component="form" onSubmit={createCampaign} sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                <Campaign color="primary" />
                <Typography variant="h6">Create campaign</Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField id="campaign-title" name="title" label="Notification title" value={form.title} onChange={update('title')} required inputProps={{ maxLength: 80 }} helperText={`${form.title.length}/80`} />
                <TextField id="campaign-message" name="body" label="Message" value={form.body} onChange={update('body')} required multiline minRows={3} inputProps={{ maxLength: 500 }} helperText={`${form.body.length}/500`} />

                {/* ─── Image / GIF attachment ─────────────────────────── */}
                <Box>
                  <input
                    ref={fileInputRef}
                    id="campaign-image"
                    name="image"
                    aria-label="Campaign image or GIF"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={handleImagePick}
                  />
                  {imagePreview ? (
                    <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Attachment preview"
                        sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      />
                      {imageUploading && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <CircularProgress size={32} />
                        </Box>
                      )}
                      {!imageUploading && imageUploadUrl && (
                        <Chip size="small" color="success" label="Uploaded" sx={{ position: 'absolute', top: 6, left: 6 }} />
                      )}
                      <IconButton size="small" onClick={clearImage} sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<AddPhotoAlternate />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ py: 1.5, borderStyle: 'dashed' }}
                    >
                      Attach image or GIF (optional)
                    </Button>
                  )}
                  {imageError && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{imageError}</Typography>}
                  {imageUploadUrl && !imageUploading && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                      Attachment URL: {imageUploadUrl}
                    </Typography>
                  )}
                </Box>

                <FormControl fullWidth>
                  <InputLabel id="campaign-category-label">Campaign type</InputLabel>
                  <Select id="campaign-category" name="category" labelId="campaign-category-label" label="Campaign type" value={form.category} onChange={update('category')}>
                    {CATEGORIES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="campaign-audience-label">Audience</InputLabel>
                  <Select id="campaign-audience" name="audience" labelId="campaign-audience-label" label="Audience" value={form.audience} onChange={update('audience')}>
                    {AUDIENCES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="campaign-zone-label">Service zone</InputLabel>
                  <Select id="campaign-zone" name="zoneId" labelId="campaign-zone-label" label="Service zone" value={form.zoneId} onChange={update('zoneId')}>
                    <MenuItem value="">All zones</MenuItem>
                    {zones.map((zone) => (
                      <MenuItem key={String(zone._id)} value={String(zone._id)}>
                        {zone.name} · {zone.city} · {Number(zone.radius || 5)} km
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  id="campaign-schedule"
                  name="scheduledAt"
                  label="Schedule for later (optional)"
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={update('scheduledAt')}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) }}
                />
                <Alert severity="info" icon={<Schedule />}>
                  {preview ? <><strong>{preview.eligible.toLocaleString()}</strong> devices have a registered push token{form.zoneId ? ` inside ${zones.find((zone) => zone._id === form.zoneId)?.name || 'the selected zone'}` : ''}. {preview.withoutToken.toLocaleString()} matching accounts cannot receive push notifications yet.</> : 'Checking audience size…'}
                </Alert>
                <Typography variant="caption" color="text.secondary">
                  FCM reports whether it accepted each message, not whether it appeared on a device. Campaign results show accepted and failed sends.
                </Typography>
                <Button type="submit" variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Send />} disabled={saving || !preview?.eligible || imageUploading}>
                  {saving ? 'Saving…' : form.scheduledAt ? 'Schedule campaign' : 'Send now'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2.5, pb: 1.5 }}>
                <Typography variant="h6">Campaign history</Typography>
                <Typography variant="body2" color="text.secondary">Schedules are stored and processed by the server, including after a restart.</Typography>
              </Box>
              {loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box> : campaigns.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3 }}>No campaigns have been created.</Typography>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Campaign</TableCell><TableCell>Audience</TableCell><TableCell>Status</TableCell><TableCell>FCM result</TableCell><TableCell>Scheduled</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{campaigns.map((campaign) => (
                      <TableRow key={campaign._id} hover>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {campaign.imageUrl && (
                              <Box component="img" src={campaign.imageUrl} alt="" sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }} />
                            )}
                            <Box>
                              <Typography fontWeight={700} noWrap>{campaign.title}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">{campaign.body}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{AUDIENCE_LABEL[campaign.audience] || campaign.audience}{campaign.zoneName && <Typography variant="caption" color="text.secondary" display="block">{campaign.zoneName}</Typography>}</TableCell>
                        <TableCell><Chip size="small" color={statusColor(campaign.status)} label={campaign.status} /></TableCell>
                        <TableCell><Typography variant="body2" color="success.main">{campaign.sentCount || 0} accepted</Typography><Typography variant="caption" color="error.main">{campaign.failedCount || 0} failed</Typography></TableCell>
                        <TableCell>{formatDate(campaign.scheduledAt)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={0.5} alignItems="center">
                            <Button size="small" onClick={() => openDetails(campaign)}>Details</Button>
                            {campaign.status === 'scheduled' && (
                              <Tooltip title="Cancel scheduled campaign">
                                <Button size="small" color="warning" onClick={() => cancelCampaign(campaign)}>Cancel</Button>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete campaign">
                              <IconButton size="small" color="error" onClick={() => deleteCampaign(campaign)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Campaign details dialog ─── */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Campaign details
            <IconButton size="small" onClick={() => setSelected(null)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selected && <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{selected.title}</Typography>
              <Chip size="small" color={statusColor(selected.status)} label={selected.status} />
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{selected.body}</Typography>
            {selected.imageUrl && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Attached image</Typography>
                <Box component="img" src={selected.imageUrl} alt="Campaign attachment" sx={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              </Box>
            )}
            {selected.zoneName && <Typography variant="body2" sx={{ mb: 2 }}>Zone: <b>{selected.zoneName}</b></Typography>}
            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mb: 2 }}>
              <Typography variant="body2">Eligible: <b>{selected.eligibleCount || 0}</b></Typography>
              <Typography variant="body2" color="success.main">FCM accepted: <b>{selected.sentCount || 0}</b></Typography>
              {typeof selected.displayedCount === 'number' && selected.displayedCount > 0 && (
                <Typography variant="body2" color="info.main">Displayed: <b>{selected.displayedCount}</b></Typography>
              )}
              {typeof selected.openedCount === 'number' && selected.openedCount > 0 && (
                <Typography variant="body2" color="primary.main">Opened: <b>{selected.openedCount}</b></Typography>
              )}
              <Typography variant="body2" color="error.main">Failed: <b>{selected.failedCount || 0}</b></Typography>
              <Typography variant="body2" color="text.secondary">No token: <b>{selected.noTokenCount || 0}</b></Typography>
            </Stack>
            {selected.errorSummary && <Alert severity="warning" sx={{ mb: 2 }}>{selected.errorSummary}</Alert>}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Account ID</TableCell><TableCell>Audience</TableCell><TableCell>Status</TableCell><TableCell>Attempts</TableCell><TableCell>Message / error</TableCell><TableCell>Time</TableCell></TableRow></TableHead>
                <TableBody>{deliveries.map((delivery) => (
                  <TableRow key={delivery._id}>
                    <TableCell>{String(delivery.recipientId).slice(-8)}</TableCell>
                    <TableCell>{delivery.recipientRole}</TableCell>
                    <TableCell>
                      {delivery.status === 'opened' ? (
                        <Chip size="small" color="primary" label="Opened" />
                      ) : delivery.status === 'displayed' ? (
                        <Chip size="small" color="info" label="Displayed" />
                      ) : delivery.status === 'received' ? (
                        <Chip size="small" color="secondary" label="Device received" />
                      ) : delivery.status === 'sent' ? (
                        <Chip size="small" color="success" label="FCM accepted" />
                      ) : (
                        <Chip size="small" color="error" label="Failed" />
                      )}
                    </TableCell>
                    <TableCell>{delivery.attempts}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflowWrap: 'anywhere' }}>{delivery.providerMessageId || delivery.error || '—'}</TableCell>
                    <TableCell>{formatDate(delivery.openedAt || delivery.displayedAt || delivery.attemptedAt)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
            {deliveries.length === 0 && <Typography color="text.secondary" sx={{ py: 2 }}>Delivery records will appear when the campaign starts.</Typography>}
          </>}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Button color="error" startIcon={<Delete />} onClick={() => deleteCampaign(selected)}>
            Delete campaign
          </Button>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
